/* eslint no-unused-vars: 0 */
import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { Popover, Transition } from "@headlessui/react";
import { use100vh } from "react-div-100vh";

import {
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/solid";

import { classNames } from "@/helpers/classNames";

import getNextLanguageLink from "@/components/languages/get-next-language-link";
import LinkWrapper from "@/components/linkWrapper";
import MediaWrapper from "@/components/mediaWrapper";
import Accordion from "@/components/Accordion";
import ContactFormModal from "@/components/blocks/contact/modal";
import { sendCustomEvent } from "@/components/plausible";

export const query = graphql`
  fragment NavbarWithFullWidthFlyoutsData on StrapiComponentNavbarWithFullWidthFlyouts {
    companyLogo {
      ...StrapiSharedLink
    }
    openMenuText
    closeMenuText

    menu1Title
    submenu1Title {
      ...StrapiSharedLink
    }
    submenu1Links {
      ...StrapiSharedLink
    }
    submenu2Title {
      ...StrapiSharedLink
    }
    submenu2Links {
      ...StrapiSharedLink
    }
    submenu3Title {
      ...StrapiSharedLink
    }
    submenu3Links {
      ...StrapiSharedLink
    }
    submenu4Title {
      ...StrapiSharedLink
    }
    submenu4Links {
      ...StrapiSharedLink
    }

    menu2Title
    menu2Links {
      ...StrapiSharedLink
    }
    menu3Title
    menu3Links {
      ...StrapiSharedLink
    }
    menubarLinks {
      ...StrapiSharedLink
    }

    infobarLinks {
      strapiName
      ...StrapiSharedLink
    }
    infobarModalContactFormCloseText
    infobarModalContactForm {
      ...ContactCenteredData
    }
    infobarModalArrangeMeetingUrl
  }
`;

function WithFullWidthFlyouts({
  data,
  locale,
  languageLinks = null,
  switchBackground = true,
}) {
  if (!data || !locale) {
    return <></>;
  }

  const [isTop, setIsTop] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [dialogContactFormOpen, setDialogContactFormOpen] = useState(false);
  const screenHeight = use100vh();

  function infobarLinkOnClick(link) {
    link = String(link);
    const plausibleEvent = link?.match(/^https:\/\/calendly\.com\//)
      ? "calendly"
      : link?.match(/^tel:/)
        ? "phone"
        : link?.match(/^mailto:/)
          ? "mail"
          : "";
    sendCustomEvent(plausibleEvent);
  }

  useEffect(() => {
    const scrolled = typeof window !== "undefined" && window.scrollY < 20;
    if (scrolled !== isTop) {
      setIsTop(scrolled);
    }

    if (typeof document !== "undefined") {
      document.addEventListener(
        "scroll",
        () => {
          const scrolled = typeof window !== "undefined" && window.scrollY < 20;
          if (scrolled !== isTop) {
            setIsTop(scrolled);
          }
        },
        { passive: true }
      );
    }
  });

  const nextLanguageLink = getNextLanguageLink({
    currentLanguage: locale,
    languageLinks,
  });

  const menu1submenus = [
    ...(data.submenu1Title || data.submenu1Links
      ? [{ title: data.submenu1Title, links: data.submenu1Links }]
      : []),
    ...(data.submenu2Title || data.submenu2Links
      ? [{ title: data.submenu2Title, links: data.submenu2Links }]
      : []),
    ...(data.submenu3Title || data.submenu3Links
      ? [{ title: data.submenu3Title, links: data.submenu3Links }]
      : []),
    ...(data.submenu4Title || data.submenu4Links
      ? [{ title: data.submenu4Title, links: data.submenu4Links }]
      : []),
  ];

  const menus2and3 = [
    ...(data.menu2Title
      ? [{ title: data.menu2Title, links: data.menu2Links }]
      : []),
    ...(data.menu3Title
      ? [{ title: data.menu3Title, links: data.menu3Links }]
      : []),
  ];

  const mobileMenues = [...menu1submenus, ...menus2and3];

  return (
    <div id="modal-root" className="relative">
      <ContactFormModal
        open={dialogContactFormOpen}
        setOpen={setDialogContactFormOpen}
        classNameBackground="bg-background-primary"
        srTextClose={data.infobarModalContactFormCloseText}
        data={data.infobarModalContactForm}
        formVersion={data.infobarModalContactForm?.version}
      />
      {!switchBackground && (
        <div className="relative bg-background-white lg:h-20" />
      )}
      <Popover className="fixed inset-x-0 top-0 z-30">
        <div
          className="pointer-events-none absolute inset-0 z-30"
          aria-hidden="true"
        />
        {!isTop && Boolean(data.infobarLinks?.length) && (
          <div className="w-full bg-background-primary text-text-on-primary">
            <div className="-mx-2 flex max-w-7xl justify-end gap-8 px-4 sm:px-6 lg:mx-auto">
              {Boolean(data.infobarLinks?.length) &&
                data.infobarLinks.map(
                  (item, index) =>
                    (item.overwriteTitle || item.link?.title) &&
                    (item.strapiName?.toLowerCase() === "modal contact form" ? (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setDialogContactFormOpen(true)}
                        className={classNames(
                          "filter-color-on-primary flex items-center justify-center gap-1 px-2 py-2 hover:text-text-on-primary-dark lg:p-0"
                        )}
                      >
                        {(item.overwriteIcon || item.link?.icon) && (
                          <MediaWrapper
                            media={item.overwriteIcon || item.link?.icon}
                            objectFit="contain"
                            divClassName="h-7 w-7 flex-shrink-0 lg:h-4 lg:w-4"
                            aria-hidden="true"
                          />
                        )}
                        <div
                          className={classNames(
                            "sr-only ml-1",
                            (item.overwriteIcon || item.link?.icon) &&
                              "lg:not-sr-only"
                          )}
                        >
                          <span className="font-[Poppins,sans-serif] text-base font-normal">
                            {item.overwriteTitle || item.link?.title}
                          </span>
                        </div>
                      </button>
                    ) : (
                      <LinkWrapper
                        key={index}
                        to={item.link?.url}
                        className={classNames(
                          "flex items-center justify-center gap-1 px-2 py-2 hover:text-text-on-primary-dark lg:p-0"
                        )}
                        onClick={() => infobarLinkOnClick(item.link?.url)}
                      >
                        {(item.overwriteIcon || item.link?.icon) && (
                          <MediaWrapper
                            media={item.overwriteIcon || item.link?.icon}
                            objectFit="contain"
                            divClassName="h-7 w-7 flex-shrink-0 lg:h-4 lg:w-4"
                            aria-hidden="true"
                          />
                        )}
                        <div
                          className={classNames(
                            "sr-only ml-1",
                            (item.overwriteIcon || item.link?.icon) &&
                              "lg:not-sr-only"
                          )}
                        >
                          <span className="font-[Poppins,sans-serif] text-base font-normal">
                            {item.overwriteTitle || item.link?.title}
                          </span>
                        </div>
                      </LinkWrapper>
                    ))
                )}
            </div>
          </div>
        )}
        <div
          className={`${
            !isTop || isOpen || !switchBackground
              ? "bg-background-white shadow"
              : "bg-transparent text-text-white"
          } relative z-20`}
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-5 sm:px-6 sm:py-4 lg:justify-start lg:space-x-10 lg:px-8">
            <div>
              {(data.companyLogo?.overwriteIcon ||
                data.companyLogo?.link?.icon) && (
                <LinkWrapper
                  to={data.companyLogo?.link?.url || "/"}
                  className="flex"
                >
                  {(data.companyLogo?.overwriteTitle ||
                    data.companyLogo?.link?.title) && (
                    <span className="sr-only">
                      {data.companyLogo?.overwriteTitle ||
                        data.companyLogo?.link?.title}
                    </span>
                  )}
                  <MediaWrapper
                    media={
                      data.companyLogo?.overwriteIcon ||
                      data.companyLogo?.link?.icon
                    }
                    divClassName={`${
                      !isTop || isOpen || !switchBackground ? "" : "inverted"
                    } h-8`}
                    width="auto"
                    objectFit="contain"
                  />
                </LinkWrapper>
              )}
            </div>
            <div className="-my-2 -mr-2 lg:hidden">
              <Popover.Button className="inline-flex items-center justify-center rounded-md p-2 hover:text-text-gray focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring-primary">
                <span className="sr-only">
                  {data.openMenuText || "Open menu"}
                </span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </Popover.Button>
            </div>
            <div className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-between">
              <Popover.Group as="nav" className="flex items-center space-x-10">
                {data.menu1Title && Boolean(menu1submenus?.length) && (
                  <Popover>
                    {({ open }) => {
                      setIsOpen(open);
                      return (
                        <>
                          <Popover.Button
                            className={classNames(
                              open
                                ? !isTop || isOpen
                                  ? "text-text-gray"
                                  : "text-text-light-gray"
                                : "",
                              !isTop || isOpen
                                ? "hover:text-text-gray"
                                : "hover:text-text-light-gray",
                              "group inline-flex items-center rounded-md bg-transparent text-base font-medium"
                            )}
                          >
                            <span>{data.menu1Title}</span>
                            <ChevronDownIcon
                              className={classNames(
                                open
                                  ? !isTop || isOpen
                                    ? "text-text-gray"
                                    : "text-text-light-gray"
                                  : "",
                                !isTop || isOpen
                                  ? "group-hover:text-text-gray"
                                  : "group-hover:text-text-light-gray",
                                "ml-2 h-5 w-5 shrink-0"
                              )}
                              aria-hidden="true"
                            />
                          </Popover.Button>

                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 -translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 -translate-y-1"
                          >
                            <Popover.Panel className="absolute inset-x-0 top-full z-10 hidden transform shadow-lg lg:block">
                              <div className="absolute inset-0 flex">
                                <div className="w-full bg-background-white" />
                              </div>
                              <div className="relative mx-auto grid max-w-7xl grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                                {menu1submenus.map((item, index) => (
                                  <nav
                                    key={index}
                                    className="grid gap-y-10 bg-background-white px-4 py-8  sm:gap-x-8 sm:px-6 sm:py-12 lg:px-8 xl:pr-12"
                                  >
                                    <div>
                                      {(item.title?.overwriteTitle ||
                                        item.title?.link?.title) && (
                                        <LinkWrapper
                                          to={item.title?.link?.url}
                                          className="redacted text-lg font-medium uppercase tracking-wide text-text-white"
                                        >
                                          <span className="hover:bg-background-secondary">
                                            {item.title?.overwriteTitle ||
                                              item.title?.link?.title}
                                          </span>
                                        </LinkWrapper>
                                      )}
                                      <ul
                                        role="list"
                                        className="mt-5 space-y-6"
                                      >
                                        {Boolean(item.links?.length) &&
                                          item.links.map(
                                            (link, linkIndex) =>
                                              (link.overwriteTitle ||
                                                link.link?.title) && (
                                                <li
                                                  key={linkIndex}
                                                  className="flow-root"
                                                >
                                                  <LinkWrapper
                                                    to={link.link?.url}
                                                    className="-my-3 flex items-center rounded-md p-3 text-base font-medium text-text-dark-gray hover:bg-background-off-white"
                                                  >
                                                    <span className="">
                                                      {link.overwriteTitle ||
                                                        link.link?.title}
                                                    </span>
                                                  </LinkWrapper>
                                                </li>
                                              )
                                          )}
                                      </ul>
                                    </div>
                                  </nav>
                                ))}
                              </div>
                            </Popover.Panel>
                          </Transition>
                        </>
                      );
                    }}
                  </Popover>
                )}
                {Boolean(menus2and3?.length) &&
                  menus2and3.map(
                    (item, index) =>
                      Boolean(item.links?.length) && (
                        <Popover key={index} className="relative z-50">
                          {({ open }) => (
                            <>
                              <Popover.Button
                                className={classNames(
                                  open
                                    ? !isTop || isOpen
                                      ? "text-text-gray"
                                      : "text-text-light-gray"
                                    : "",
                                  !isTop || isOpen
                                    ? "hover:text-text-gray"
                                    : "hover:text-text-light-gray",
                                  "group inline-flex items-center rounded-md text-base font-medium"
                                )}
                              >
                                <span>{item.title}</span>
                                <ChevronDownIcon
                                  className={classNames(
                                    open
                                      ? !isTop || isOpen
                                        ? "text-text-gray"
                                        : "text-text-light-gray"
                                      : "",
                                    !isTop || isOpen
                                      ? "group-hover:text-text-gray"
                                      : "group-hover:text-text-light-gray",
                                    "ml-2 h-5 w-5 shrink-0"
                                  )}
                                  aria-hidden="true"
                                />
                              </Popover.Button>

                              <Transition
                                as={Fragment}
                                enter="transition ease-out duration-200"
                                enterFrom="opacity-0 translate-y-1"
                                enterTo="opacity-100 translate-y-0"
                                leave="transition ease-in duration-150"
                                leaveFrom="opacity-100 translate-y-0"
                                leaveTo="opacity-0 translate-y-1"
                              >
                                <Popover.Panel className="absolute left-1/2 z-50 mt-3 w-screen max-w-md -translate-x-1/2 transform px-2 sm:px-0">
                                  <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-ring-black ring-opacity-5">
                                    <div className="relative grid gap-6 bg-background-white px-5 py-6 sm:gap-8 sm:p-8">
                                      {item.links.map(
                                        (link, index) =>
                                          (link.overwriteTitle ||
                                            link.link?.title) && (
                                            <LinkWrapper
                                              key={index}
                                              to={link.link?.url}
                                              className="-m-3 flex items-start rounded-lg p-3 hover:bg-background-off-white"
                                            >
                                              <div className="ml-4">
                                                <span className="text-base font-medium text-text-primary custom-font">
                                                  {link.overwriteTitle ||
                                                    link.link?.title}
                                                </span>
                                              </div>
                                            </LinkWrapper>
                                          )
                                      )}
                                    </div>
                                  </div>
                                </Popover.Panel>
                              </Transition>
                            </>
                          )}
                        </Popover>
                      )
                  )}
                {Boolean(data.menubarLinks?.length) &&
                  data.menubarLinks?.map(
                    (item, index) =>
                      (item.overwriteTitle || item.link?.title) && (
                        <LinkWrapper
                          key={index}
                          to={item.link?.url}
                          className={classNames(
                            !isTop || isOpen
                              ? "hover:text-text-gray"
                              : "hover:text-text-light-gray",
                            "text-base font-medium"
                          )}
                        >
                          {item.overwriteTitle || item.link?.title}
                        </LinkWrapper>
                      )
                  )}
              </Popover.Group>
              {nextLanguageLink && (
                <div className="flex items-center md:ml-12">
                  <LinkWrapper
                    to={nextLanguageLink.url}
                    className={`${
                      !isTop || isOpen || !switchBackground
                        ? "text-text-black hover:border-text-gray hover:text-text-gray"
                        : "text-text-white hover:border-text-light-gray hover:text-text-light-gray"
                    } inline-flex items-center justify-center rounded-md border bg-transparent px-4 py-2 text-base font-medium shadow-sm`}
                  >
                    {nextLanguageLink.nativeName}
                  </LinkWrapper>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* MOBILE */}
        <Transition
          as={Fragment}
          enter="duration-200 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            focus
            className="absolute inset-x-0 top-0 z-30 origin-top-right transform p-2 text-text-primary transition lg:hidden"
            style={{ zIndex: 1000 }} /* without z-index hidden behind hero */
            tabIndex={-1} /* without tab-index not clickable in Safari*/
          >
            <div
              className="divide-y-2 divide-divide-light overflow-auto overscroll-contain rounded-lg bg-background-white shadow-lg ring-1 ring-ring-black ring-opacity-5"
              style={{ maxHeight: screenHeight ? screenHeight - 16 : "92vh" }}
            >
              <div className="px-5 pb-6 pt-5 sm:pb-8">
                <div className="flex items-center justify-between">
                  <div>
                    {(data.companyLogo?.overwriteIcon ||
                      data.companyLogo?.link?.icon) && (
                      <MediaWrapper
                        media={
                          data.companyLogo?.overwriteIcon ||
                          data.companyLogo?.link?.icon
                        }
                        divClassName="h-8 w-auto"
                        objectFit="contain"
                      />
                    )}
                  </div>
                  <div className="-mr-2">
                    <Popover.Button className="inline-flex items-center justify-center rounded-md bg-background-white p-2 text-text-secondary hover:bg-background-light-gray hover:text-text-secondary-dark focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring-primary">
                      <span className="sr-only">
                        {data.closeMenuText || "Close menu"}
                      </span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </Popover.Button>
                  </div>
                </div>
                <div className="mt-6 h-2/3 sm:mt-8">
                  <nav className="bg-background-white py-8 sm:px-6 sm:py-12 lg:px-8 xl:pr-12">
                    {Boolean(mobileMenues?.length) &&
                      mobileMenues.map(
                        (item, index) =>
                          Boolean(item.links?.length) && (
                            <Accordion
                              key={index}
                              title={
                                item.title?.overwriteTitle ||
                                item.title?.link?.title ||
                                item.title
                              }
                            >
                              {item.links.map(
                                (link, linkIndex) =>
                                  (link.overwriteTitle || link.link?.title) && (
                                    <div
                                      key={linkIndex}
                                      className="transition-max-height my-2 overflow-auto duration-700 ease-in-out"
                                    >
                                      <LinkWrapper to={link.link?.url}>
                                        {link.overwriteTitle ||
                                          link.link?.title}
                                      </LinkWrapper>
                                    </div>
                                  )
                              )}
                            </Accordion>
                          )
                      )}
                    {/* 
                  <Accordion key={index} title={item.title}>
                    {item.pageLinks.map((pageLink, pageIndex) => (
                      <div key={pageIndex} className="my-2">
                        <LinkWrapper to={pageLink.page.url}>
                          {pageLink.page.title}
                        </LinkWrapper>
                      </div>
                    ))}
                  </Accordion>
                   */}
                    {Boolean(data.menubarLinks?.length) &&
                      data.menubarLinks.map(
                        (item, index) =>
                          (item.overwriteTitle || item.link?.title) && (
                            <LinkWrapper key={index} to={item.link?.url}>
                              <div className="flex w-full items-center justify-between border border-l-0 border-r-0 border-t-0 border-border-light-gray p-3 text-left font-medium text-text-primary hover:bg-background-light-gray">
                                {item.overwriteTitle || item.link?.title}
                              </div>
                            </LinkWrapper>
                          )
                      )}
                  </nav>
                </div>
              </div>
              {nextLanguageLink && (
                <div className="px-5 py-6">
                  <div>
                    <p className="text-center text-base font-medium text-text-secondary">
                      <LinkWrapper
                        to={nextLanguageLink.url}
                        className="flex w-full items-center justify-center rounded-md border border-transparent bg-background-primary px-4 py-2 text-base font-medium text-text-white shadow-sm hover:bg-background-primary-dark"
                      >
                        {nextLanguageLink.nativeName}
                      </LinkWrapper>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  );
}

WithFullWidthFlyouts.propTypes = {
  data: PropTypes.object,
  locale: PropTypes.string,
  languageLinks: PropTypes.object,
};

export default WithFullWidthFlyouts;
